<template>
  <component
      :is="props.tag"
      class="heading-resizing"
  >
    <span
        v-for="(item, idx) in state.segments"
        :key="`span_${idx}`"
        ref="elements"
        class="heading-resizing__elements"
        :class="[item.emphasized ? 'heading-resizing__elements--emphasized' : 'heading-resizing__elements--regular', {'heading-resizing__elements--indented': state.indented}]"
    >
      <span class="heading-resizing__segment" v-html="item.label"></span>
    </span>
  </component>
</template>

<script setup lang="ts">

import {nextTick, onMounted, onUnmounted, reactive, ref, watch} from 'vue'
import {useAnimatedTitleStore} from "~/store/animated-title";

const props = defineProps({
  isMounted: Boolean,
  initialized: Boolean,
  tag: {type: String, default: 'h1', required: false},
  label: String,
  animate: Boolean,
  indented: Boolean,
  randomDelay: Boolean
})

const state = reactive({
  isMounted: false,
  initialized: false,
  segments: [props.label,],
  interval: 0,
  timeout: 0,
  animate: props.animate,
  indented: props.indented
})

const elements = ref([])
const animatedTitleStore = useAnimatedTitleStore()

let itemsInReversedOrder = []

// setup
const label = props.label.toString()
const result = label.split(/\s?\{\{(.+)\}\}\s?/gm).filter(e => e)
const emphasized = label.replace(/.*?\{\{(.+)\}\}.*/gm, '$1')
state.segments = result.map((item) => {
  return {label: item, emphasized: item === emphasized, fittie: null}
})

function onAnimate(toInitialState = false) {

  let nextY = 0
  let bugged = false

  itemsInReversedOrder.forEach((el) => {
    const elWidth = el.offsetWidth
    const elHeight = el.offsetHeight
    const elInnerSpan = el.getElementsByTagName('span')[0]
    const elInnerSpanWidth = elInnerSpan.offsetWidth
    let scaleFactor = elWidth / elInnerSpanWidth

    if (elInnerSpanWidth === 0 || elWidth === 0) {
      bugged = true
    } else {
      if ((el.classList.contains('heading-resizing__elements--emphasized') && !toInitialState)
          || (!el.classList.contains('heading-resizing__elements--emphasized') && toInitialState)
      ) {
        scaleFactor = 1
      }

      el.setAttribute('style', `transform: translateY(${nextY}px) scale(${scaleFactor})`)
      nextY += elHeight - elHeight * scaleFactor
    }
  })

  if (bugged) {
    window.setTimeout(() => {
      onAnimate(toInitialState)
    }, 1000 / 60)
  }
}

function initAnimation(intervalTime = 3000, toInitialStateDelayTime = 350) {
  setInterval(() => onAnimate(), intervalTime)
  setTimeout(
      () => {
        setInterval(() => onAnimate(true), intervalTime)
      },
      toInitialStateDelayTime
  )
}

const main = () => {
  nextTick(() => {

    if (props.label.split(' ').length === 1) {
      state.animate = false
      state.segments[0].emphasized = true
    }
    itemsInReversedOrder = elements.value.reverse()

    onAnimate(true)

    if (state.animate) {
      initAnimation()
    }

    state.initialized = true
  })
}

onMounted(() => {
  state.isMounted = true

  if (animatedTitleStore.fontsReady) {
    if (!state.initialized) {
      if (!props.randomDelay) {
        main()
      } else {
        window.setTimeout(main, Math.random() * 1000)
      }
    }
  }
})

watch(() => animatedTitleStore.fontsReady, () => {
  if (state.isMounted && !state.initialized) {
    if (!props.randomDelay) {
      main()
    } else {
      window.setTimeout(main, Math.random() * 1000)
    }
  }
}, {immediate: true})

onUnmounted(() => {

  window.clearInterval(state.interval)
  state.initialized = false
  state.isMounted = false
})
</script>

<style scoped lang="postcss">
.heading-resizing {
  &__elements {
    display: block;
    hyphens: auto;
  }

  &__segment {
    display: inline-block;
    hyphens: auto;
  }

  &__elements--regular, &__elements--emphasized {
    transform-origin: bottom left;
    transition: all 0.3s ease;
    /* rendering improvements for Safari */
    -webkit-font-smoothing: antialiased;
    /* latest chrome and edge only */
    text-wrap: balance;

    :deep(div) {
      display: inline-block;
    }
  }
}

.heading-resizing__elements--indented.heading-resizing__elements--regular {
  @media (--lg) {
    margin-left: calc(100% / 12);
  }

  @media (--xl) {
    margin-left: calc(100% / 6);
    margin-right: calc(100% / 12);
  }
}

.heading-resizing__elements--emphasized .heading-resizing__segment {
  transform: translateY(7%);
}

.heading-resizing__elements--indented.heading-resizing__elements--emphasized .heading-resizing__segment {
  transform: translateY(3%);
}
</style>
